// =============================================================================
// VulnTracker — Organizations & Projects Page
// =============================================================================
import { useState } from 'react';
import {
  useOrganizations,
  useProjects,
  useCreateOrganization,
  useUpdateOrganization,
  useDeleteOrganization,
  useLinkProjectToOrg,
} from '@/api/hooks';
import { Pagination } from '@/components/Pagination';
import type { Organization, Project } from '@/types';
import {
  BuildingOfficeIcon,
  PlusIcon,
  XMarkIcon,
  LinkIcon,
  FolderIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

export function OrganizationsPage() {
  const [orgPage, setOrgPage] = useState(1);
  const [projPage, setProjPage] = useState(1);
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  // --- Add Organization form state ---
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgDesc, setNewOrgDesc] = useState('');

  // --- Link Project modal state ---
  const [linkingOrgId, setLinkingOrgId] = useState<string | null>(null);
  const [linkProjectId, setLinkProjectId] = useState('');

  // --- Edit Organization modal state ---
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [editOrgName, setEditOrgName] = useState('');
  const [editOrgDesc, setEditOrgDesc] = useState('');

  // --- Delete Organization confirm state ---
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);

  const { data: orgs, isLoading: orgsLoading } = useOrganizations({
    page: orgPage,
    page_size: 20,
  });

  const { data: projects, isLoading: projLoading } = useProjects({
    page: projPage,
    page_size: 100,
    ...(selectedOrg && { organization_id: selectedOrg }),
  });

  // All projects (unfiltered) for the link dropdown
  const { data: allProjects } = useProjects({ page: 1, page_size: 100 });

  const createOrgMutation = useCreateOrganization();
  const updateOrgMutation = useUpdateOrganization();
  const deleteOrgMutation = useDeleteOrganization();
  const linkMutation = useLinkProjectToOrg();

  const openEditOrg = (org: Organization) => {
    setEditingOrg(org);
    setEditOrgName(org.name);
    setEditOrgDesc(org.description || '');
  };

  const handleUpdateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrg || !editOrgName.trim()) return;
    try {
      await updateOrgMutation.mutateAsync({
        orgId: editingOrg.id,
        data: { name: editOrgName.trim(), description: editOrgDesc.trim() },
      });
      setEditingOrg(null);
    } catch {
      // error handled by mutation state
    }
  };

  const handleDeleteOrg = async () => {
    if (!deletingOrg) return;
    try {
      await deleteOrgMutation.mutateAsync(deletingOrg.id);
      if (selectedOrg === deletingOrg.id) setSelectedOrg('');
      setDeletingOrg(null);
    } catch {
      // error handled by mutation state
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;
    try {
      await createOrgMutation.mutateAsync({
        name: newOrgName.trim(),
        description: newOrgDesc.trim() || undefined,
      });
      setNewOrgName('');
      setNewOrgDesc('');
      setShowAddForm(false);
    } catch {
      // error handled by mutation state
    }
  };

  const handleLinkProject = async () => {
    if (!linkingOrgId || !linkProjectId) return;
    try {
      await linkMutation.mutateAsync({
        projectId: linkProjectId,
        organizationId: linkingOrgId,
      });
      setLinkingOrgId(null);
      setLinkProjectId('');
    } catch {
      // error handled by mutation state
    }
  };

  // Projects belonging to the selected org (for display inside org card)
  const orgProjects = (orgId: string) =>
    (allProjects?.items ?? []).filter((p: Project) => p.organization_id === orgId);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BuildingOfficeIcon className="h-7 w-7 text-primary-600" />
            Organizations
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage organizations and link projects to them. {orgs?.total ?? 0} organization{(orgs?.total ?? 0) !== 1 ? 's' : ''} total.
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Add Organization
        </button>
      </div>

      {/* ── Add Organization Form ─────────────────────────────────────── */}
      {showAddForm && (
        <div className="card border-2 border-primary-200 bg-primary-50/30">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">New Organization</h2>
            <button
              onClick={() => setShowAddForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleCreateOrg} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                placeholder="e.g. Security Division"
                className="input w-full max-w-md"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={newOrgDesc}
                onChange={(e) => setNewOrgDesc(e.target.value)}
                placeholder="Optional description..."
                className="input w-full max-w-md"
                rows={2}
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={createOrgMutation.isPending || !newOrgName.trim()}
                className="btn btn-primary"
              >
                {createOrgMutation.isPending ? 'Creating...' : 'Create Organization'}
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
            {createOrgMutation.isError && (
              <p className="text-sm text-red-600">
                Failed to create organization. {(createOrgMutation.error as any)?.response?.data?.detail || 'Please try again.'}
              </p>
            )}
          </form>
        </div>
      )}

      {/* ── Organization Cards ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {orgsLoading ? (
          <div className="flex items-center justify-center h-32 col-span-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : (orgs?.items ?? []).length === 0 ? (
          <div className="col-span-full text-center py-16">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-300" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900">No organizations</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new organization.</p>
          </div>
        ) : (
          (orgs?.items ?? []).map((org: Organization) => {
            const linkedProjects = orgProjects(org.id);
            const isSelected = selectedOrg === org.id;
            return (
              <div
                key={org.id}
                className={`card transition-all ${
                  isSelected ? 'ring-2 ring-primary-500 shadow-lg' : 'hover:shadow-md'
                }`}
              >
                {/* Org Header */}
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1 cursor-pointer"
                    onClick={() => {
                      setSelectedOrg(isSelected ? '' : org.id);
                      setProjPage(1);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <BuildingOfficeIcon className="h-5 w-5 text-primary-600" />
                      <h3 className="font-semibold text-gray-900 text-lg">{org.name}</h3>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {org.description || 'No description'}
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Created {new Date(org.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => {
                        setLinkingOrgId(org.id);
                        setLinkProjectId('');
                      }}
                      className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                      title="Link a project"
                    >
                      <LinkIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => openEditOrg(org)}
                      className="p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                      title="Edit organization"
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setDeletingOrg(org)}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete organization"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Linked Projects */}
                {linkedProjects.length > 0 && (
                  <div className="mt-4 border-t pt-3">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                      Linked Projects ({linkedProjects.length})
                    </p>
                    <div className="space-y-1">
                      {linkedProjects.map((proj: Project) => (
                        <div
                          key={proj.id}
                          className="flex items-center gap-2 px-3 py-2 rounded-md bg-gray-50 text-sm"
                        >
                          <FolderIcon className="h-4 w-4 text-gray-400" />
                          <span className="font-medium text-gray-800">{proj.name}</span>
                          <span className="text-xs text-gray-400 ml-auto">
                            {proj.component_count} components
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {linkedProjects.length === 0 && (
                  <div className="mt-4 border-t pt-3 text-center">
                    <p className="text-xs text-gray-400">No projects linked yet</p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {orgs && orgs.total_pages > 1 && (
        <Pagination
          page={orgPage}
          totalPages={orgs.total_pages}
          onPageChange={setOrgPage}
        />
      )}

      {/* ── Link Project Modal ────────────────────────────────────────── */}
      {linkingOrgId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Link Project to Organization</h2>
              <button
                onClick={() => setLinkingOrgId(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Select a project to link to{' '}
              <span className="font-semibold text-gray-700">
                {(orgs?.items ?? []).find((o: Organization) => o.id === linkingOrgId)?.name}
              </span>
            </p>
            <select
              value={linkProjectId}
              onChange={(e) => setLinkProjectId(e.target.value)}
              className="input w-full mb-4"
            >
              <option value="">— Select a project —</option>
              {(allProjects?.items ?? [])
                .filter((p: Project) => p.organization_id !== linkingOrgId)
                .map((p: Project) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.organization_id ? ' (already linked)' : ''}
                  </option>
                ))}
            </select>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setLinkingOrgId(null)}
                className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleLinkProject}
                disabled={!linkProjectId || linkMutation.isPending}
                className="btn btn-primary"
              >
                {linkMutation.isPending ? 'Linking...' : 'Link Project'}
              </button>
            </div>
            {linkMutation.isError && (
              <p className="text-sm text-red-600 mt-2">
                Failed to link project. {(linkMutation.error as any)?.response?.data?.detail || 'Please try again.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Edit Organization Modal ──────────────────────────────────── */}
      {editingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Edit Organization</h2>
              <button
                onClick={() => setEditingOrg(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateOrg} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editOrgName}
                  onChange={(e) => setEditOrgName(e.target.value)}
                  className="input w-full"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={editOrgDesc}
                  onChange={(e) => setEditOrgDesc(e.target.value)}
                  className="input w-full"
                  rows={3}
                />
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => setEditingOrg(null)}
                  className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateOrgMutation.isPending || !editOrgName.trim()}
                  className="btn btn-primary"
                >
                  {updateOrgMutation.isPending ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
              {updateOrgMutation.isError && (
                <p className="text-sm text-red-600">
                  Failed to update. {(updateOrgMutation.error as any)?.response?.data?.detail || 'Please try again.'}
                </p>
              )}
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Organization Confirm Modal ─────────────────────────── */}
      {deletingOrg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-red-700">Delete Organization</h2>
              <button
                onClick={() => setDeletingOrg(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-gray-900">{deletingOrg.name}</span>?
            </p>
            <p className="text-xs text-red-600 font-medium mb-2">
              ⚠️ This will permanently delete:
            </p>
            <ul className="text-xs text-gray-600 mb-6 ml-4 space-y-1">
              <li>• All projects in this organization</li>
              <li>• All components in those projects</li>
              <li>• All vulnerability matches</li>
            </ul>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeletingOrg(null)}
                className="btn bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteOrg}
                disabled={deleteOrgMutation.isPending}
                className="btn bg-red-600 text-white hover:bg-red-700"
              >
                {deleteOrgMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
            {deleteOrgMutation.isError && (
              <p className="text-sm text-red-600 mt-2">
                Failed to delete. {(deleteOrgMutation.error as any)?.response?.data?.detail || 'Please try again.'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* ── Projects Table (filtered by selected org) ─────────────────── */}
      {selectedOrg && (
        <>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Projects in Organization
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {projects?.total ?? 0} project{(projects?.total ?? 0) !== 1 ? 's' : ''}
            </p>
          </div>

          <div className="card overflow-hidden p-0">
            {projLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Repository
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Components
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(projects?.items ?? []).map((proj: Project) => (
                      <tr key={proj.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <p className="font-medium text-gray-900">{proj.name}</p>
                          <p className="text-xs text-gray-500">{proj.description}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {proj.repository_url ? (
                            <a
                              href={proj.repository_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:underline"
                            >
                              {proj.repository_url}
                            </a>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {proj.component_count}
                        </td>
                      </tr>
                    ))}
                    {(projects?.items ?? []).length === 0 && (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                          No projects in this organization
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {projects && projects.total_pages > 1 && (
                  <Pagination
                    page={projPage}
                    totalPages={projects.total_pages}
                    onPageChange={setProjPage}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
